import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { loginWithClientUsernamePassword, loginWithToken } from '~/api/auth';
import { fetchCsrfToken } from '~/api/csrf';
import { sendForgotPasswordEmail } from '~/api/login';
import evisortConversationalAI from '~/assets/images/login/evisort-conversational-ai.gif';
import AuthenticationStore from '~/auth/authenticationStore';
import { trackAnalyticsEvent } from '~/common/analytics/analyticsClient';
import { getDecodedUserInfoFromToken } from '~/components/LoginPage/utils';
import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import { LoginViewType } from '~/enums/LoginViewType';
import { QueryParamType } from '~/enums/QueryParamType';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { parseWorkspace, setSearchParams } from '~/routing/utils';
import { SUCCESS } from '~/types/toast.types';
import { copyToClipboard } from '~/utils/helper.utils';
import {
  getPageSearchQuery,
  getPageSearchQueryByKey,
} from '~/utils/searchQuery';

const AS = AuthenticationStore();

const welcomeBanner = {
  banner: '',
  bannerImageBackgroundSize: '100%',
  bannerImagePosition: 'center',
  bannerImageSrc: evisortConversationalAI,
  padding: 0,
};

export const useAegisLoginProps = (props) => {
  const { ssoConfigs, stage, error, setStage, setError } = props;
  const workspace = parseWorkspace(props.workspace);
  const { navigate } = useRouting();
  const intl = useIntl();

  const [oAuthRedirectTo, setOAuthRedirectTo] = useState(null);
  const [resetPasswordQueryResult, setResetPasswordQueryResult] = useState({});
  const [isLoadingLoginAccount, setIsLoadingLoginAccount] = useState(false);

  const isEmbed = AS.isEmbed();
  const isSsoOnly = isEmbed && ssoConfigs; // enable auto-login if in embedded login mode and ssoConfigs exists
  const searchParamKey = [...getPageSearchQuery().keys()][0];
  const samlErrorMsg = getPageSearchQueryByKey(QueryParamType.SamlError);

  const loginErrorMessage = {
    ACCOUNT_LOCKED: intl.formatMessage({
      id: 'UseAegisLoginProps.accountLockedError',
      defaultMessage:
        'You have failed too many times. Try again in 30 minutes.',
      description:
        'Error message when account is locked due to failed attempts',
    }),
    INVALID_CREDENTIALS: intl.formatMessage({
      id: 'UseAegisLoginProps.invalidCredentialsError',
      defaultMessage: 'Invalid email or password.',
      description: 'Error message for invalid login credentials',
    }),
    INVALID_TOKEN: intl.formatMessage({
      id: 'UseAegisLoginProps.invalidTokenError',
      defaultMessage: 'Invalid token.',
      description: 'Error message for invalid authentication token',
    }),
    SAML_ERROR: intl.formatMessage({
      id: 'UseAegisLoginProps.samlError',
      defaultMessage:
        "Failed to log in using the supplied credentials. Contact your organization's administrator to verify that your account is set up correctly.  Click the button to copy the error code.",
      description: 'Error message for SAML authentication failure',
    }),
    SERVER_ERROR: intl.formatMessage({
      id: 'UseAegisLoginProps.serverError',
      defaultMessage: 'An error occurred. Please try again later.',
      description: 'Generic server error message',
    }),
    SSO_ENFORCED: intl.formatMessage({
      id: 'UseAegisLoginProps.ssoEnforcedError',
      defaultMessage:
        "Single sign-on is enforced for your organization and you must log in using your organization's identity provider. Please click on the Sign In button above to log in.",
      description: 'Error message when SSO is enforced for the organization',
    }),
  };

  // login initialization
  useEffect(() => {
    AS.setCsrf(null); // clean csrf token in auth store on login page load
  }, []);

  // auto-login with SSO-only if enabled appropriately
  useEffect(() => {
    if (isSsoOnly) handleLoginSso();
  }, [workspace, isSsoOnly]);

  useEffect(
    () => {
      const searchParamValue = getPageSearchQueryByKey(searchParamKey, '');
      const handleSAMLCallback = () => {
        if (!searchParamKey) return;

        const { client_id, user_id } = JSON.parse(atob(searchParamValue)) ?? {};

        if (client_id && user_id) {
          trackAnalyticsEvent('User SSO Login', {
            groupId: client_id,
            userId: `${user_id}_${client_id}`,
          });
        }

        // EKP-15827 when httponly enabled
        if (!AS.getAccessToken() || !AS.getRefreshToken()) {
          AS.removeInactiveTimer();
          AS.saveJwtTokenCreationTime();
        }

        setLogIn();
      };

      const handleResetPasswordCallback = (loggedInWithWelcome) => {
        loginWithToken({ token: searchParamValue })
          .then((data) => {
            // EKP-15827 when httponly not enabled
            if (data.access_token && data.refresh_token) {
              AS.saveAuthentication(data.access_token, data.refresh_token);
            }
            // EKP-15827 when httponly enabled
            if (data.user_id && data.client_id) {
              AS.removeInactiveTimer();
              AS.saveJwtTokenCreationTime();
            }
            setLogIn(loggedInWithWelcome);
          })
          .catch(() =>
            setError(
              intl.formatMessage({
                id: 'UseAegisLoginProps.loginError',
                defaultMessage: 'An error occurred with log in.',
                description: 'Generic login error message',
              }),
            ),
          );
      };

      switch (searchParamKey) {
        case QueryParamType.SamlSuccess:
          return handleSAMLCallback();
        case QueryParamType.SamlError:
          return setStage(LoginViewType.SamlError);
        case QueryParamType.FirstLoginSuccessToken:
          return handleResetPasswordCallback(true);
        case QueryParamType.ResetSuccessToken:
          return handleResetPasswordCallback();
        default:
          return undefined;
      }
    },
    [props.location.search, props.navigate], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const from = getPageSearchQueryByKey('from', '');
    const redirectTo = getPageSearchQueryByKey('redirect_to', '');

    if (from === 'teamsApp' && redirectTo) {
      const envAllowedDomains = getEnvVariable(
        'REACT_APP_ALLOWED_REDIRECT_TO_DOMAIN',
      );
      const allowedDomains = envAllowedDomains
        ? envAllowedDomains.split(',')
        : [];
      const isAllowedDomain = allowedDomains.some((domain) => {
        // The domain can be a wildcard domain, so we need to convert it to a regex pattern
        // to match the origin of the redirect_to URL.
        // e.g. https://*.devtunnels.ms -> https:\/\/.*\.devtunnels\.ms$/
        const regexPattern = domain.replace(/\./g, '\\.').replace(/\*/g, '.*');
        const redirectToOrigin = new URL(redirectTo).origin;

        // This domain 'https://.*\\.devtunnels\\.ms' should match with
        // 'https://m349ndw7-3978.us.devtunnels.ms'
        return new RegExp(`^${regexPattern}$`).test(redirectToOrigin);
      });

      if (isAllowedDomain) {
        setOAuthRedirectTo(redirectTo);
      } else {
        setError(
          intl.formatMessage({
            id: 'UseAegisLoginProps.redirectNotAllowedError',
            defaultMessage: 'The auth success redirection will not be allowed.',
            description: 'Error message when redirect is not allowed',
          }),
        );
      }
    }
  }, [props.location.search, props.navigate, intl]);

  const setLogIn = (loggedInWithWelcome = false) => {
    setTimeout(() => {
      if (AS.isLoggedIn()) {
        loggedInWithWelcome
          ? setStage(LoginViewType.LoggedInWithWelcome)
          : setStage(LoginViewType.LoggedIn);
      }
    }, 100);
  };

  const handleLogin = (formData) => {
    setIsLoadingLoginAccount(true);
    return loginWithClientUsernamePassword(formData)
      .then(async (data) => {
        // EKP-15827 when httponly not enabled
        if (data.access_token && data.refresh_token) {
          AS.saveAuthentication(data.access_token, data.refresh_token);

          const { client_id, user_id } = getDecodedUserInfoFromToken(
            data.access_token,
          );
          trackAnalyticsEvent('User Login', {
            groupId: client_id,
            userId: `${user_id}_${client_id}`,
          });
        }

        // EKP-15827 when httponly enabled
        if (data.user_id && data.client_id) {
          AS.removeInactiveTimer();
          AS.saveJwtTokenCreationTime();

          trackAnalyticsEvent('User Login', {
            groupId: data.client_id,
            userId: `${data.user_id}_${data.client_id}`,
          });
        }

        if (isEmbed) {
          try {
            if (!AS.getCsrf()) {
              await fetchCsrfToken();
            }
            navigate(RoutePathType.LoginEmbedSuccess);
            return;
          } catch {
            navigate(RoutePathType.Login);
          }
        }

        setTimeout(() => {
          if (AS.isLoggedIn()) {
            setStage(LoginViewType.LoggedIn);
          }

          if (oAuthRedirectTo) {
            window.location.href = oAuthRedirectTo;
          }
        }, 100);
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          const {
            response: {
              data: { invalid_attempts, msg },
            },
          } = error;

          if (invalid_attempts >= 3) {
            setError(
              intl.formatMessage(
                {
                  id: 'UseAegisLoginProps.loginAttemptsWarning',
                  defaultMessage:
                    'You have attempted to login {attempts} times unsuccessfully.  After 5 failed attempts, your account will be locked for 30 minutes.',
                  description:
                    'Warning message for multiple failed login attempts. Example: attempts = "3"',
                },
                { attempts: invalid_attempts },
              ),
            );
          } else {
            setError(loginErrorMessage[msg]);
          }
        }
      })
      .finally(() => setIsLoadingLoginAccount(false));
  };

  const handleLoginSso = () => {
    AS.clearAuthentication();
    if (!ssoConfigs) return;

    window.location.href = isEmbed
      ? setSearchParams(ssoConfigs.signon_url, {
          RelayState: `${window.location.origin}/${workspace}${RoutePathType.LoginEmbedSuccess}`,
        })
      : ssoConfigs.signon_url;
  };

  const handleCopySsoError = () => {
    copyToClipboard(
      samlErrorMsg,
      intl.formatMessage({
        id: 'UseAegisLoginProps.errorCodeCopied',
        defaultMessage: 'Error code copied to clipboard.',
        description: 'Success message when error code is copied',
      }),
      SUCCESS,
    );
  };

  const handleLoginAccount = (updatedAccount) => {
    handleLogin({
      client: workspace,
      username: updatedAccount.email,
      password: updatedAccount.password,
    });
  };

  const handleResetPassword = ({ email, workspace }) => {
    setResetPasswordQueryResult({ isFetching: true });
    sendForgotPasswordEmail({ client: workspace, userId: email })
      .then(() => {
        setResetPasswordQueryResult({
          isFetching: false,
          isSuccess: true,
        });
      })
      .catch(() => {
        setResetPasswordQueryResult({
          isError: true,
          isFetching: false,
        });
      });
  };

  // Not all legacy queries handle results (isLoading/isSuccess/isError);
  const queryLoginAccount = [
    handleLoginAccount,
    {
      error: stage === LoginViewType.SamlError ? undefined : error,
      isFetching: isLoadingLoginAccount,
    },
  ];
  const queryLoginSso = [
    handleLoginSso,
    {
      error:
        stage === LoginViewType.SamlError
          ? loginErrorMessage.SAML_ERROR
          : undefined,
    },
  ];
  const queryResetPassword = [handleResetPassword, resetPasswordQueryResult];

  const onBackToSignIn = () => setResetPasswordQueryResult({});

  const sso = ssoConfigs
    ? {
        id: ssoConfigs.provider_name,
        name: ssoConfigs.provider_name,
        url: ssoConfigs.signon_url,
      }
    : null;

  return {
    queryLoginAccount,
    queryLoginSso,
    queryResetPassword,
    onBackToSignIn,
    onResolveLoginSsoError: handleCopySsoError,
    sso,
    stage: stage === LoginViewType.Domain && !error ? 'workspace' : 'login', // this logic maps to the eds.LoginPage.stage.
    welcomeBanner,
  };
};
